import "@/App.css";
import "@/index.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import PortalLayout from "@/layouts/PortalLayout";
import MemberLayout from "@/layouts/MemberLayout";
import AdminLayout from "@/layouts/AdminLayout";

import Home from "@/pages/portal/Home";
import Profile from "@/pages/portal/Profile";
import Shop from "@/pages/portal/Shop";
import ProductDetail from "@/pages/portal/ProductDetail";
import Cart from "@/pages/portal/Cart";
import Register from "@/pages/portal/Register";
import RegisterStaff from "@/pages/portal/RegisterStaff";
import Login from "@/pages/portal/Login";
import News from "@/pages/portal/News";
import Catalog from "@/pages/portal/Catalog";
import PaymentResult from "@/pages/portal/PaymentResult";
import { Terms, RefundPolicy, PrivacyPolicy, Contact } from "@/pages/portal/Legal";

import MemberDashboard from "@/pages/member/Dashboard";
import MemberCard from "@/pages/member/MemberCard";
import MemberShop from "@/pages/member/MemberShop";
import Savings from "@/pages/member/Savings";
import SavingsDeposit from "@/pages/member/SavingsDeposit";
import SavingsWithdraw from "@/pages/member/SavingsWithdraw";
import SHUTracker from "@/pages/member/SHUTracker";
import MemberOrders from "@/pages/member/Orders";
import MemberSettings from "@/pages/member/Settings";
import SellerHub from "@/pages/member/SellerHub";
import ScanQR from "@/pages/member/ScanQR";

import TrackOrder from "@/pages/portal/TrackOrder";

import AdminDashboard from "@/pages/admin/Dashboard";
import AdminMembers from "@/pages/admin/Members";
import AdminFinance from "@/pages/admin/Finance";
import AdminProducts from "@/pages/admin/Products";
import AdminOrders from "@/pages/admin/Orders";
import AdminReports from "@/pages/admin/Reports";
import AdminRoles from "@/pages/admin/Roles";
import AdminPayments from "@/pages/admin/Payments";

import AdminSHU from "@/pages/admin/AdminSHU";
import RevenueSplit from "@/pages/admin/RevenueSplit";
import HomepageSettings from "@/pages/admin/HomepageSettings";
import InvestorReport from "@/pages/admin/InvestorReport";
import StaffApprovals from "@/pages/admin/StaffApprovals";
import { SHUConfigProvider } from "./lib/shuConfig";
import { AuthProvider } from "@/lib/auth";
import { CartProvider } from "@/lib/cart";
import NewsDetail from "@/pages/portal/NewsDetail";
import AdminWilayah from "@/pages/admin/Wilayah";
import AdminProcurement from "@/pages/admin/Procurement";
import InstallPrompt from "@/components/pwa/InstallPrompt";
import NetworkStatus from "@/components/pwa/NetworkStatus";

// Pindah halaman selalu mulai dari atas (seperti aplikasi), kecuali tautan ke #anchor.
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function App() {
  return (
    <div className="App bg-background min-h-screen text-foreground">
      <AuthProvider>
      <CartProvider>
      <SHUConfigProvider>
      <BrowserRouter>
        <ScrollToTop />
        <NetworkStatus />
        <InstallPrompt />
        <Toaster position="top-right" richColors closeButton />
        <Routes>
          {/* Module 1 — Portal Publik */}
          <Route element={<PortalLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/profil" element={<Profile />} />
            <Route path="/toko" element={<Shop />} />
            <Route path="/toko/:id" element={<ProductDetail />} />
            <Route path="/keranjang" element={<Cart />} />
            <Route path="/daftar" element={<Register />} />
            <Route path="/daftar-pengurus" element={<RegisterStaff />} />
            <Route path="/masuk" element={<Login />} />
            <Route path="/berita" element={<News />} />
            <Route path="/berita/:id" element={<NewsDetail />} />
            <Route path="/lacak" element={<TrackOrder />} />
            <Route path="/katalog" element={<Catalog />} />
            <Route path="/payment/success" element={<PaymentResult />} />
            <Route path="/payment/failed" element={<PaymentResult failed />} />
            <Route path="/kontak" element={<Contact />} />
            <Route path="/syarat-ketentuan" element={<Terms />} />
            <Route path="/kebijakan-pengembalian" element={<RefundPolicy />} />
            <Route path="/kebijakan-privasi" element={<PrivacyPolicy />} />
          </Route>

          {/* Module 2 — Ruang Anggota */}
          <Route path="/anggota" element={<MemberLayout />}>
            <Route index element={<MemberDashboard />} />
            <Route path="kartu" element={<MemberCard />} />
            <Route path="toko" element={<MemberShop />} />
            <Route path="scan" element={<ScanQR />} />
            <Route path="jualan" element={<SellerHub />} />
            <Route path="tabungan" element={<Savings />} />
            <Route path="tabungan/setor/:jenis" element={<SavingsDeposit />} />
            <Route path="tabungan/tarik" element={<SavingsWithdraw />} />
            <Route path="shu" element={<SHUTracker />} />
            <Route path="keranjang" element={<Cart embedded />} />
            <Route path="pesanan" element={<MemberOrders />} />
            <Route path="pengaturan" element={<MemberSettings />} />
          </Route>

          {/* Module 3 — Dasbor Manajemen */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="anggota" element={<AdminMembers />} />
            <Route path="keuangan" element={<AdminFinance />} />
            <Route path="produk" element={<AdminProducts />} />
            <Route path="pesanan" element={<AdminOrders />} />
            <Route path="pembayaran" element={<AdminPayments />} />
            <Route path="laporan" element={<AdminReports />} />
            <Route path="peran" element={<AdminRoles />} />
            <Route path="bagi-hasil" element={<RevenueSplit />} />
            <Route path="shu" element={<AdminSHU />} />
            <Route path="homepage" element={<HomepageSettings />} />
            <Route path="investor" element={<InvestorReport />} />
            <Route path="pengurus" element={<StaffApprovals />} />
            <Route path="wilayah" element={<AdminWilayah />} />
            <Route path="pengadaan" element={<AdminProcurement />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </SHUConfigProvider>
      </CartProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
